import DashboardSkeleton from "./DashboardSkeleton";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { WEB_API_DASHBOARD } from "../../env";
import { encodeBase64 } from "../../utils/convertBase64";
import { StaticImage } from "../../utils/image";
import ApiLaw from "../../api/law";
import { WatDatePicker } from "thaidatepicker-react";
import moment from "moment";
import ApiMaster from "../../api/master";
import Select from "react-select";
import Swal from "sweetalert2";

const FilterSelect = ({ label, options, value, onChange, className = "" }) => {
    const normalizedOptions = options.map((option) => (
        typeof option === "string" ? { value: option, label: option } : option
    ));
    const optionValueMap = useMemo(
        () => new Map(normalizedOptions.map((option) => [String(option.value ?? ""), option.value])),
        [normalizedOptions]
    );

    return (
        <label className={`flex flex-col gap-1 text-xs text-slate-500 ${className}`}>
            {label}
            <select
                value={value ?? ""}
                onChange={(event) => onChange(optionValueMap.get(event.target.value))}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-sky-400 focus:outline-none"
            >
                {normalizedOptions.map((option) => (
                    <option key={String(option.value ?? "")} value={String(option.value ?? "")}>
                        {option.label}
                    </option>
                ))}
            </select>
        </label>
    );
};

const DateFilterInput = ({ label, value, onChange }) => (
    <label className="flex flex-col gap-1 text-xs text-slate-500">
        {label}
        <WatDatePicker
            value={value}
            onChange={(christDate) => onChange(christDate ? moment(christDate) : null)}
            inputStyle={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: "8px 12px",
                fontSize: 14,
                width: "100%",
            }}
            placeholder="วว/ดด/ปป"
            dateFormat={"yyyy-MM-dd"}
            displayFormat={"DD/MM/YYYY"}
            clearable={false}
            disabled={false}
            readOnly={false}
            yearBoundary={99}
        />
    </label>
);

const StatCard = () => (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                    จำนวนกฎหมายที่เปิดรับฟังความคิดเห็น
                </p>
                <div className="mt-2 flex gap-3 justify-end">
                    <span className="text-4xl font-semibold text-slate-900">3,341</span>
                    <span className="text-xs font-semibold text-emerald-600">+19.0%</span>
                </div>
                <p className="text-xs text-slate-700 mt-1 text-right">กฎหมาย (ฉบับ)</p>
            </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 text-center text-xs text-slate-500">
            {[
                { label: "ปี 2566", value: "3,908" },
                { label: "ปี 2567", value: "1,224" },
                { label: "ปี 2568", value: "889" },
            ].map((item) => (
                <div key={item.label} className="rounded-xl bg-slate-50 px-2 py-3">
                    <p className="text-lg font-semibold text-slate-900">{item.value}</p>
                    <p className="text-[11px] text-slate-700">{item.label}</p>
                </div>
            ))}
        </div>
    </div>
);

const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "0";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString("en-US");
};

const getAgencyShort = (agencyName) => {
    if (!agencyName) return "";
    return agencyName.trim().slice(0, 2);
};

const THAI_MONTH_ORDER = new Map([
    ["มกราคม", 1], ["ม.ค.", 1],
    ["กุมภาพันธ์", 2], ["ก.พ.", 2],
    ["มีนาคม", 3], ["มี.ค.", 3],
    ["เมษายน", 4], ["เม.ย.", 4],
    ["พฤษภาคม", 5], ["พ.ค.", 5],
    ["มิถุนายน", 6], ["มิ.ย.", 6],
    ["กรกฎาคม", 7], ["ก.ค.", 7],
    ["สิงหาคม", 8], ["ส.ค.", 8],
    ["กันยายน", 9], ["ก.ย.", 9],
    ["ตุลาคม", 10], ["ต.ค.", 10],
    ["พฤศจิกายน", 11], ["พ.ย.", 11],
    ["ธันวาคม", 12], ["ธ.ค.", 12],
]);

const thaiCollator = new Intl.Collator("th", {
    sensitivity: "base",
    numeric: true,
});

const ListeningDashboard = () => {
    const history = useHistory();

    const parseDateParam = (value, fallback) => {
        if (!value) return fallback;
        const parsed = moment(value, "YYYY-MM-DD", true);
        return parsed.isValid() ? parsed : fallback;
    };

    const parseNumberParam = (value, fallback = 0) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    };

    const parseAgencyParam = (params) => {
        if (!params.has("agency_id")) return 0;

        const value = params.get("agency_id");
        if (value === null || value === "null") return null;

        return parseNumberParam(value, 0);
    };

    const getDefaultFiscalRange = () => {
        const now = moment();
        const isFiscalYearStart = now.month() >= 9;
        const startYear = isFiscalYearStart ? now.year() : now.year() - 1;
        const endYear = isFiscalYearStart ? now.year() + 1 : now.year();

        return {
            start: moment({ year: startYear, month: 9, date: 1 }),
            end: moment({ year: endYear, month: 8, date: 30 }),
        };
    };

    const defaultRange = getDefaultFiscalRange();
    const defaultParams = new URLSearchParams(window.location.search);
    // Keep the agency drill-down; the standalone menu now contains filters/export only.
    const showAgencyDetails = defaultParams.has("agency_id") || defaultParams.has("unspecified_agency");
    const [startDate, setStartDate] = useState(
        parseDateParam(defaultParams.get("start_date"), defaultRange.start)
    );
    const [endDate, setEndDate] = useState(
        parseDateParam(defaultParams.get("end_date"), defaultRange.end)
    );
    const [selectedLawType, setSelectedLawType] = useState(0);
    const [selectedAgency, setSelectedAgency] = useState(
        parseAgencyParam(defaultParams)
    );
    const [selectedMinistry, setSelectedMinistry] = useState(0);

    const [agencyOption, setAgencyOption] = useState([
        { value: 0, label: "ทุกหน่วยงาน" },
    ]);
    const [ministryOption, setMinistryOption] = useState([
        { value: 0, label: "ทุกกระทรวง" },
    ]);

    const [lawHierarchyOption, setLawHierarchyOption] = useState([
        { value: 0, label: "ทุกศักดิ์กฎหมาย" },
    ]);

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const donutSeries = [11.56, 39.18, 14.1, 9.41, 9.14, 8.0, 8.61];
    const donutLabels = [
        "รัฐธรรมนูญ",
        "พระราชบัญญัติ",
        "พระราชกฤษฎีกา",
        "พระราชกำหนด",
        "กฎกระทรวง",
        "ประกาศ",
        "อื่นๆ",
    ];
    const donutColors = ["#2563eb", "#f97316", "#10b981", "#ec4899", "#a855f7", "#facc15", "#64748b"];

    const donutOptions = useMemo(
        () => ({
            chart: {
                type: "donut",
                toolbar: { show: false },
                fontFamily: "Prompt, 'Noto Sans Thai', sans-serif",
            },
            labels: donutLabels,
            colors: donutColors,
            dataLabels: { enabled: false },
            legend: { show: false },
            plotOptions: {
                pie: {
                    donut: {
                        size: "70%",
                        labels: {
                            show: true,
                            value: {
                                fontSize: "20px",
                                fontWeight: 600,
                                formatter: (value) => `${value}%`,
                            },
                            total: {
                                show: true,
                                label: "รวมทั้งหมด",
                                fontSize: "12px",
                                color: "#94a3b8",
                                formatter: () => "100%",
                            },
                        },
                    },
                },
            },
        }),
        []
    );

    const [dashboardData, setDashboardData] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchDashboardData = useCallback(async () => {
        // Swal.showLoading();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                start_date: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                lawType: selectedLawType ?? "",
                agency_id: selectedAgency,
                ministry_id: selectedMinistry ?? 0,
            };

            const response = await fetch(`${WEB_API_DASHBOARD}/dga-user-service-survey/dashboard/answer-dashboard`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setDashboardData(data.agencyListeningRows);
        } catch (err) {
            console.error("Error fetching agency dashboard data:", err);
            setError(err.message);
        } finally {
            // Swal.close();
            setLoading(false);
        }
    }, [startDate, endDate, selectedLawType, selectedAgency, selectedMinistry]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    useEffect(() => {
        getLawHierarchyOption();
        getFilterOption();
    }, []);

    const getLawHierarchyOption = async () => {
        const result = await ApiLaw.getMaster();
        if (result.status == 200) {
            const { data } = result;
            const hierarchy = Array.isArray(data?.hirachy) ? data.hirachy : [];
            setLawHierarchyOption([
                { value: 0, label: "ทุกศักดิ์กฎหมาย" },
                ...hierarchy,
            ]);
        }
    };

    const getFilterOption = async () => {
        const filter = await ApiMaster.homeFilter();
        if (filter.status == 200) {
            const { data } = filter.data;
            setAgencyOption([...agencyOption, ...data.agency]);
            setMinistryOption([...ministryOption, ...data.ministry]);
        }
    };

    const rangeLabel = useMemo(() => {
        if (!startDate || !endDate) {
            return "ทุกช่วงเวลา";
        }
        return `${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`;
    }, [startDate, endDate]);

    const csvEscape = (value) => {
        if (value === null || value === undefined) return "";
        const text = String(value);
        if (/[",\n]/.test(text)) {
            return `"${text.replace(/"/g, '""')}"`;
        }
        return text;
    };

    const handleDownloadExcel = () => {
        const rows = [
            ["เดือน", "หน่วยงาน", "หัวข้อรับฟังความคิดเห็น", "ศักดิ์กฎหมาย", "ความคิดเห็น (ครั้ง)"],
            ...tableRows.map((row) => [
                row.month,
                row.agency,
                row.title,
                row.lawType,
                row.count ?? 0,
            ]),
        ];
        const csvContent = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
        const blob = new Blob(["\ufeff", csvContent], {
            type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `listening-dashboard-${moment().format(
            "YYYYMMDD"
        )}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [startDate, endDate, selectedLawType, dashboardData]);

    const tableRows = useMemo(() => {
        if (!dashboardData) {
            return [];
        }
        return dashboardData.map((row, index) => ({
            key: `${row.survey_name}-${index}`,
            month: String(row.month_th || "-").trim(),
            monthOrder: Number(row.month_active) || THAI_MONTH_ORDER.get(String(row.month_th || "").trim()) || 13,
            agency: String(row.agency_name || "-").trim(),
            agencyShort: getAgencyShort(row.agency_name),
            title: String(row.survey_name || "-").trim(),
            lawType: row.law_hierarchy_name || "-",
            count: row.answer_per_question ?? 0,
            surveyId: row.survey_id,
        })).sort((a, b) => {
            const monthDiff = a.monthOrder - b.monthOrder;
            if (monthDiff !== 0) return monthDiff;

            const agencyDiff = thaiCollator.compare(a.agency, b.agency);
            if (agencyDiff !== 0) return agencyDiff;

            return thaiCollator.compare(a.title, b.title);
        });
    }, [dashboardData]);

    const totalPages = useMemo(() => {
        if (!tableRows.length) {
            return 1;
        }
        return Math.ceil(tableRows.length / pageSize);
    }, [tableRows.length]);

    const pagedRows = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return tableRows.slice(startIndex, startIndex + pageSize);
    }, [currentPage, tableRows]);

    const totalAnswerCount = useMemo(() => {
        if (!dashboardData) {
            return 0;
        }
        return dashboardData.reduce((sum, row) => {
            const value = Number(row.answer_per_question);
            return sum + (Number.isNaN(value) ? 0 : value);
        }, 0);
    }, [dashboardData]);

    const selectedMinistryLabel = useMemo(() => {
        if (!selectedMinistry) {
            return "ทุกกระทรวง";
        }
        return ministryOption.find((option) => option.value == selectedMinistry)?.label || "ทุกกระทรวง";
    }, [ministryOption, selectedMinistry]);

    const selectedAgencyLabel = useMemo(() => {
        if (!selectedAgency) {
            return "ทุกหน่วยงาน";
        }
        return agencyOption.find((option) => option.value == selectedAgency)?.label || "ทุกหน่วยงาน";
    }, [agencyOption, selectedAgency]);


    const selectedLawLabel = useMemo(() => {
        return lawHierarchyOption.find((option) => option.value == selectedLawType)?.label || "ทุกศักดิ์กฎหมาย";
    }, [lawHierarchyOption, selectedLawType]);

    // Loading state
    if (loading) {
        return <DashboardSkeleton variant="listening" />;
    }

    // Error state
    if (error && !dashboardData) {
        return (
            <div className="min-h-screen bg-[#f3f6fb] pb-16 pt-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <div className="text-red-500 mb-4">
                                <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <p className="text-sm text-slate-500">เกิดข้อผิดพลาด: {error}</p>
                            <button
                                onClick={fetchDashboardData}
                                className="mt-4 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm hover:bg-sky-600"
                            >
                                ลองใหม่อีกครั้ง
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }


    return (
        <div className="min-h-screen bg-[#f3f6fb] pb-16 pt-24">
            <div className="mx-auto max-w-7xl space-y-6 px-4 sm:px-6 lg:px-8">
                <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex items-center gap-x-2">
                            <button
                                onClick={() => history.go(-1)}
                                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-3 py-1 text-xs font-medium text-sky-600 hover:bg-slate-50"
                            >
                                <svg viewBox="0 0 24 24" className="h-4 w-4">
                                    <path
                                        d="M15 18l-6-6 6-6"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                ย้อนกลับ
                            </button>
                            <p className="text-sm text-primary font-bold">สถิติรายการการรับฟัง และปริมาณความคิดเห็น</p>
                            <button
                                type="button"
                                onClick={handleDownloadExcel}
                                disabled={tableRows.length === 0}
                                className={`mb-[1px] inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${tableRows.length === 0
                                    ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                                    : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                    }`}
                            >
                                ดาวน์โหลด Excel
                            </button>
                        </div>
                    </div>
                    <div className="grid gap-3 md:grid-cols-5">
                        <DateFilterInput
                            label="วันที่เริ่มต้น"
                            value={startDate}
                            onChange={setStartDate}
                        />
                        <DateFilterInput
                            label="วันที่สิ้นสุด"
                            value={endDate}
                            onChange={setEndDate}
                        />
                        {/* <FilterSelect
                            label="แยกตามศักดิ์กฎหมาย"
                            options={lawHierarchyOption}
                            value={selectedLawType}
                            onChange={setSelectedLawType}
                        /> */}
                        <div className="flex items-end gap-2">
                            <label className={`flex flex-col gap-1 text-xs text-slate-500 w-full`}>
                                {"ศักดิ์กฏหมาย"}
                                <Select
                                    className="basic-single"
                                    classNamePrefix="select"
                                    defaultValue={lawHierarchyOption[0]}
                                    options={lawHierarchyOption}
                                    value={lawHierarchyOption.find((x) => x.value == selectedLawType)}
                                    onChange={(item) => {
                                        setSelectedLawType(item.value);
                                    }}
                                />
                            </label>

                        </div>

                        <label className={`flex flex-col gap-1 text-xs text-slate-500`}>
                            {"กระทรวง"}
                            <Select
                                // className="basic-single"
                                classNames={{
                                    control: (state) =>
                                        state.isFocused ? 'rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-sky-400 focus:outline-none' : 'rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-sky-400 focus:outline-none',
                                }}
                                classNamePrefix="select"
                                defaultValue={ministryOption[0]}
                                value={ministryOption.find((x) => x.value == selectedMinistry)}
                                options={ministryOption}
                                onChange={(item) => {
                                    setSelectedMinistry(item.value);
                                    setSelectedAgency(0);
                                }}
                            />
                        </label>
                        <label className={`flex flex-col gap-1 text-xs text-slate-500`}>
                            {"หน่วยงาน"}
                            <Select
                                className="basic-single"
                                classNamePrefix="select"
                                defaultValue={agencyOption[0]}
                                options={
                                    selectedMinistry > 0
                                        ? agencyOption.filter((x) => x.ministry_id == selectedMinistry)
                                        : agencyOption
                                }
                                value={agencyOption.find((x) => x.value == selectedAgency)}
                                onChange={(item) => {
                                    setSelectedAgency(item.value);
                                }}
                            />
                        </label>

                    </div>
                </section>
                {showAgencyDetails && <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between flex-1">
                            {/* <div className="flex items-center gap-x-10">
                                <p className=""><span className="text-md font-bold text-slate-900 text-bold">ปี {selectedYear}</span><span className="weight-200 text-sm !font-normal ml-2 text-slate-600">มีการรับฟังความคิดเห็น</span>
                                    <span className="text-md text-bold font-bold text-slate-900 ml-2">{formatNumber(totalAnswerCount)}</span>
                                    <span className="!font-normal ml-2 text-slate-600">ครั้ง</span></p>
                            </div> */}
                            <div className="flex gap-x-2">
                                <span>ผลลัพธ์การกรองข้อมูล</span>
                                <img className="h-4 w-4" src={StaticImage("/images/icon_v3/blue/calendar-lines-duotone.svg")} />
                                <span className="text-bold">{rangeLabel} {selectedLawLabel} {selectedAgencyLabel} {selectedMinistryLabel}</span>
                                <span>มีการรับฟังความคิดเห็น <span className="text-bold">{formatNumber(totalAnswerCount)}</span> ครั้ง</span>
                            </div>
                            <button onClick={() => {
                                history.push("/dashboard/draftlaw")
                            }} className="inline-flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-700">
                                <img className="h-4 w-4" src={StaticImage("/images/icon_v3/yellow/chart-mixed-duotone .svg")} />
                                ดูข้อมูลสถิติจากการรับฟังแยกตามศักดิ์กฎหมาย
                            </button>
                        </div>
                        {/* <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                            <FilterSelect
                                label="กรุณาเลือกเดือน"
                                options={["ทุกเดือน", "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน"]}
                                value={selectedMonth}
                                onChange={setSelectedMonth}
                            />
                            <FilterSelect
                                label="ศักดิ์กฎหมาย"
                                options={["ทุกศักดิ์", ...donutLabels]}
                                value={selectedLawType}
                                onChange={setSelectedLawType}
                            />
                        </div> */}
                    </div>
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-xs uppercase tracking-wide text-slate-700">
                                <tr>
                                    <th className="py-3 w-[100px]">เดือน</th>
                                    <th className="py-3 w-[100px]">หน่วยงาน</th>
                                    <th className="py-3">หัวข้อรับฟังความคิดเห็น</th>
                                    <th className="py-3">ศักดิ์กฎหมาย</th>
                                    <th className="py-3 text-center">ความคิดเห็น (ครั้ง)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedRows.map((row, idx) => (
                                    <tr
                                        key={`${row.title}-${idx}`}
                                        className="border-t border-slate-100 hover:bg-slate-50"
                                    >
                                        <td className="py-3 text-slate-500">{row.month}</td>
                                        <td className="py-3">
                                            <div className="flex items-center gap-3 whitespace-nowrap">
                                                <span className="text-sm text-slate-800">{row.agency}</span>
                                            </div>
                                        </td>
                                        <td
                                            className="py-3 text-sky-600 hover:text-sky-800 hover:underline cursor-pointer"
                                            onClick={() => {
                                                if (row.surveyId) {
                                                    history.push(`/listeningDetail?survey_id=${encodeBase64(row.surveyId)}`);
                                                }
                                            }}
                                        >
                                            {row.title}
                                        </td>
                                        <td className="py-3 text-slate-700">{row.lawType}</td>
                                        <td className="py-3 text-center text-slate-900 font-semibold">{formatNumber(row.count)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
                        <span>
                            แสดง {formatNumber(tableRows.length ? (currentPage - 1) * pageSize + 1 : 0)}-{formatNumber(Math.min(currentPage * pageSize, tableRows.length))} จาก {formatNumber(tableRows.length)} รายการ
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                className="rounded-lg border border-slate-200 px-3 py-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                ก่อนหน้า
                            </button>
                            <span>
                                หน้า {formatNumber(currentPage)} / {formatNumber(totalPages)}
                            </span>
                            <button
                                type="button"
                                className="rounded-lg border border-slate-200 px-3 py-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                ถัดไป
                            </button>
                        </div>
                    </div>
                </section>}
            </div >
        </div >
    );
};

export default ListeningDashboard;
